import { useTranslation } from "react-i18next";
import MoneyAmount from "./money-amount";
import { Currency } from "@/types";
import { PercentageWhiteCircleIcon } from "@/shared/ui/icons/percentage-white-circle-icon";

interface SubventionBannerProps {
  amount: number;
  currency: Currency | string;
  language?: string;
  isVisible: boolean;
  hasZeroFeesOrWaivedFees?: boolean;
  /** When true, show only "This plan has 0% APR" (multi-tenure subvention). */
  zeroAprOnly?: boolean;
}

export default function SubventionBanner({
  amount,
  currency,
  language = "en_US",
  isVisible,
  hasZeroFeesOrWaivedFees = false,
  zeroAprOnly = false,
}: SubventionBannerProps) {
  const { t } = useTranslation();

  const shouldShow = zeroAprOnly ? isVisible : isVisible && amount > 0;

  return (
    <div
      className="absolute inset-x-0 overflow-hidden z-0"
      style={{
        top: shouldShow ? "-32px" : "0px",
        maxHeight: shouldShow ? "200px" : "0px",
        pointerEvents: shouldShow ? "auto" : "none",
        transition:
          "top 500ms cubic-bezier(0.4, 0, 0.2, 1), max-height 500ms cubic-bezier(0.4, 0, 0.2, 1)",
      }}
    >
      <div
        className="flex items-center justify-center gap-1 px-[10px] pt-[9px] pb-[28px] border-[0.5px] border-[#DAE0EB] rounded-t-[22px]"
        style={{
          background:
            "linear-gradient(181deg, #128351 -12.03%, #06B769 41.19%)",
        }}
      >
        <div className="flex items-center justify-center gap-1 w-full">
          <div>
            <PercentageWhiteCircleIcon className="w-3 h-3" />
          </div>
          {zeroAprOnly ? (
            <span className="text-white font-['Plus_Jakarta_Sans'] text-[10px] font-extrabold leading-4 tracking-[0.5px]">
              {t("subvention.zero_apr")}
            </span>
          ) : hasZeroFeesOrWaivedFees ? (
            <span className="text-white uppercase font-['Plus_Jakarta_Sans'] text-[10px] font-extrabold leading-4 tracking-[0.5px]">
              {t("subvention.zero_additional_cost")}
            </span>
          ) : (
            <>
              <span className="text-white uppercase font-['Plus_Jakarta_Sans'] text-[10px] font-extrabold leading-4 tracking-[0.5px]">
                {t("subvention.save")}
              </span>
              <span className="text-white font-['Plus_Jakarta_Sans'] text-[10px] font-extrabold leading-4 tracking-[0.5px]">
                <MoneyAmount
                  amount={amount}
                  currency={currency}
                  language={language}
                  className="inline-flex"
                  amountClassName="text-inherit uppercase"
                  currencyClassName="text-inherit"
                  showCurrency={true}
                  gapClassName="gap-1"
                />
              </span>
              <span className="text-white uppercase font-['Plus_Jakarta_Sans'] text-[10px] font-extrabold leading-4 tracking-[0.5px]">
                {t("subvention.with_this_plan")}
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
